import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { useTier } from "@/context/TierContext";
import { getPlanCta } from "@/lib/planCta";
import { SiteFooter } from "@/components/SiteFooter";
import {
  Camera, ShieldCheck, ArrowRight, CheckCircle2, Loader2, Home, Wrench,
  KeyRound, ClipboardList, FolderOpen, Fingerprint, Lock, Eye, EyeOff,
  Download, MessageSquare, Receipt, Sparkles, ShieldQuestion, ImageOff,
} from "lucide-react";

const SITE_URL = "https://tenantseal.com";
const PURPLE = "#7C3AED"; // reserved exclusively for TenantSeal Plus

// ---- Section 2: scattered records (neutral scenarios; no fault/validity judgement) ----
const SCATTER = [
  { icon: ImageOff, title: "A wall-damage charge", body: "The move-in and move-out photos of that wall live in different places in your camera roll — hard to line up when it matters." },
  { icon: Wrench, title: "A repair request", body: "The message you sent, the landlord's reply, your photos and the follow-ups are spread across texts, email and memory." },
  { icon: Receipt, title: "A deduction letter", body: "The charges you were sent changed between versions — and the earlier one is buried in your inbox." },
];

// ---- Section 3: real lifecycle stages (plain language, real tools) ----
const LIFECYCLE = [
  { icon: Home, phase: "Move in", body: "A guided, room-by-room photo walkthrough builds a timestamped baseline of how the place looked on day one." },
  { icon: ClipboardList, phase: "During the tenancy", body: "Keep repairs, messages, rent records, meter readings and condition snapshots together as the tenancy goes on." },
  { icon: KeyRound, phase: "Move out", body: "Document the final condition, compare it to move-in, and record keys and meters as you hand back the place." },
  { icon: FolderOpen, phase: "Prepare a response when needed", body: "If something needs a reply, organize the relevant records and draft from what you actually documented." },
];

// ---- Section 4: product proof (real screenshots + one honest sharing control) ----
const PROOF = [
  { title: "Document what happened", body: "Capture rooms with the active property always in view, guided shot lists, and gentle prompts when a photo could be stronger.", img: "/screens/capture.png", alt: "TenantSeal capture screen for 34 Final Way showing a guided shot list and retake suggestion" },
  { title: "Build one rental record", body: "Everything you document lands in a single chronological record — sealed states and dates shown plainly.", img: "/screens/demo_timeline.png", alt: "Tenancy Timeline showing dated, sealed move-in entries in a sample workspace" },
  { title: "Prepare when something needs a response", body: "Map a landlord's charges to the exhibits you documented. TenantSeal preserves the text — it does not decide whether a charge is fair or valid.", img: "/screens/demo_storyboard.png", alt: "Charge Storyboard mapping sample charges to exhibits in a sample workspace" },
];

export default function Landing() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { openUpgrade, isPremium, trialEligible, trialing } = useTier();
  const planCta = getPlanCta({ trialEligible, trialing });
  const reduce = useReducedMotion();
  const rise = reduce ? {} : { initial: { opacity: 0, y: 16 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.45 } };

  const track = (event, source) => {
    try { api.post("/events/track", { event, source, path: window.location.pathname }).catch(() => {}); } catch { /* fire-and-forget */ }
  };
  const start = (source = "hero") => { track("landing_cta_click", source); if (!loading) navigate(user ? "/start" : "/login?next=%2Fstart"); };
  const sample = (source = "hero") => { track("landing_demo_click", source); navigate("/demo"); };
  const onPlusClick = () => (!user ? navigate("/login") : isPremium ? navigate("/dashboard") : openUpgrade("multiple_properties"));

  const StartBtn = ({ testId, source = "hero", className = "" }) => (
    <Button data-testid={testId} onClick={() => start(source)} disabled={loading}
      className={`bg-brand hover:bg-brand-hover text-white rounded-xl transition-all active:scale-[0.98] ${className}`}>
      {loading ? <Loader2 size={18} className="mr-2 animate-spin" /> : <Camera size={18} className="mr-2" />}
      Start documenting <ArrowRight size={17} className="ml-2" />
    </Button>
  );
  const SampleBtn = ({ testId, source = "hero", dark = false, className = "" }) => (
    <Button data-testid={testId} variant="outline" onClick={() => sample(source)}
      className={`rounded-xl font-medium ${dark ? "border-white/25 text-white hover:bg-white/10 bg-transparent" : "border-navy/20 text-navy hover:bg-navy/5"} ${className}`}>
      <Eye size={17} className="mr-2" /> Explore sample property
    </Button>
  );

  return (
    <div className="min-h-screen bg-[#0B1220] text-white flex flex-col">
      {/* SEO — React 19 native metadata */}
      <title>TenantSeal | Your Rental. Your Record.</title>
      <meta name="description" content="TenantSeal is a tenant-controlled rental record. Document your rental from move-in to move-out, keep important events organized, and decide exactly what you share." />
      <link rel="canonical" href={`${SITE_URL}/`} />
      <meta name="theme-color" content="#0B1220" />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="TenantSeal" />
      <meta property="og:title" content="TenantSeal | Your Rental. Your Record." />
      <meta property="og:description" content="Built for tenants — not landlords. Document your rental from move-in to move-out, keep important events organized, and decide exactly what you share." />
      <meta property="og:url" content={`${SITE_URL}/`} />
      <meta property="og:image" content={`${SITE_URL}/og-image.png?v=4`} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="TenantSeal | Your Rental. Your Record." />
      <meta name="twitter:description" content="Built for tenants — not landlords. Document your rental, keep events organized, and decide exactly what you share." />
      <meta name="twitter:image" content={`${SITE_URL}/og-image.png?v=4`} />

      {/* Nav */}
      <header className="sticky top-0 z-30 border-b border-white/10 bg-[#0B1220]/85 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Logo dark size={26} />
          <div className="flex items-center gap-2">
            {!loading && !user && (
              <Button data-testid="landing-login-btn" variant="ghost" onClick={() => navigate("/login")} className="text-white/70 hover:text-white hover:bg-white/10">Log in</Button>
            )}
            {!loading && (
              <Button data-testid="landing-cta-nav" onClick={() => start("nav")} className="bg-brand hover:bg-brand-hover text-white rounded-lg">
                {user ? "Dashboard" : "Start documenting"}
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* 1 — Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full bg-brand/20 blur-[120px]" aria-hidden="true" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-14 sm:pt-20 pb-14 grid lg:grid-cols-2 gap-12 items-center relative">
          <motion.div initial={reduce ? false : { opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span data-testid="hero-eyebrow" className="inline-flex items-center gap-2 rounded-full border border-brand/40 bg-brand/10 px-3 py-1 text-xs font-semibold text-[#9FC0F0]">
              <ShieldCheck size={14} /> Built for tenants — not landlords
            </span>
            <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
              Your rental. <span className="text-brand-foreground text-[#6FA2F0]">Your record.</span>
            </h1>
            <p data-testid="hero-hook" className="mt-5 text-xl font-semibold text-white/90">Your landlord keeps records. Now you can too.</p>
            <p className="mt-4 text-lg leading-relaxed text-white/70 max-w-lg">
              Document your rental from move-in to move-out, keep important events organized, and decide exactly what you share.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-3">
              <StartBtn testId="start-documentation-button" source="hero" className="h-14 px-8 text-base shadow-[0_10px_30px_rgba(30,77,140,0.45)]" />
              <SampleBtn testId="view-demo-btn" source="hero" dark className="h-14 px-6" />
            </div>
            <p data-testid="hero-trust-line" className="mt-5 flex items-start gap-2 text-sm text-white/60 max-w-lg">
              <Lock size={15} className="mt-0.5 shrink-0 text-white/40" /> Your original records remain private unless you deliberately share them.
            </p>
          </motion.div>

          <motion.div initial={reduce ? false : { opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.55, delay: 0.1 }} className="relative">
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-2 shadow-[0_30px_80px_rgba(0,0,0,0.5)]">
              <img src="/screens/property.png" loading="eager" width="1440" height="900"
                alt="TenantSeal property dashboard for 34 Final Way with Right now, Document, My records and Prepare & respond sections"
                className="w-full h-auto rounded-xl border border-white/10" data-testid="hero-screenshot" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* 2 — Scattered rental records */}
      <section className="bg-white text-[#0F172A]" data-testid="scattered-records">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20 w-full">
          <div className="max-w-3xl">
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-navy">Your rental history should not live in your camera roll, inbox, and memory.</h2>
          </div>
          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {SCATTER.map((s, i) => (
              <motion.div key={s.title} {...rise} transition={{ ...(rise.transition || {}), delay: i * 0.06 }} data-testid={`scatter-card-${i}`}
                className="rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-6">
                <span className="grid place-items-center h-11 w-11 rounded-xl bg-brand/10 text-brand"><s.icon size={22} /></span>
                <h3 className="mt-4 font-heading text-lg font-semibold text-navy">{s.title}</h3>
                <p className="mt-2 text-[#475569] leading-relaxed text-[15px]">{s.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 3 — Rental lifecycle */}
      <section className="bg-[#0F1B2D] text-white" data-testid="rental-lifecycle">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20 w-full">
          <div className="max-w-2xl">
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">One record across the whole tenancy</h2>
            <p className="mt-3 text-lg text-white/70">Not every tenancy ends in a dispute — but every tenancy is worth documenting.</p>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {LIFECYCLE.map((c, i) => (
              <motion.div key={c.phase} {...rise} transition={{ ...(rise.transition || {}), delay: i * 0.06 }} data-testid={`lifecycle-card-${i}`}
                className="rounded-2xl border border-white/10 bg-white/[0.03] p-6">
                <span className="grid place-items-center h-11 w-11 rounded-xl bg-brand/15 text-[#6FA2F0]"><c.icon size={22} /></span>
                <h3 className="mt-4 font-heading text-lg font-semibold">{c.phase}</h3>
                <p className="mt-2 text-white/65 leading-relaxed text-[15px]">{c.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 4 — Product proof */}
      <section className="bg-white text-[#0F172A]" data-testid="product-proof">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20 w-full">
          <div className="max-w-2xl">
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-navy">See it in the product</h2>
            <p className="mt-3 text-lg text-[#475569]">Real screens from TenantSeal — sample screens use a clearly-labeled sample workspace.</p>
          </div>
          <div className="mt-10 space-y-12">
            {PROOF.map((p, i) => (
              <motion.div key={p.title} {...rise} data-testid={`proof-row-${i}`}
                className={`grid lg:grid-cols-2 gap-8 items-center ${i % 2 ? "lg:[&>*:first-child]:order-2" : ""}`}>
                <div className="rounded-2xl border border-[#E2E8F0] bg-[#0F1B2D] p-2 shadow-lg overflow-hidden">
                  <img src={p.img} loading="lazy" alt={p.alt} className="w-full h-auto rounded-xl" data-testid={`proof-img-${i}`} />
                </div>
                <div>
                  <h3 className="font-heading text-2xl font-semibold text-navy">{p.title}</h3>
                  <p className="mt-3 text-[#475569] leading-relaxed">{p.body}</p>
                </div>
              </motion.div>
            ))}

            {/* Sharing control — explicit Original / Redacted / Excluded (real product behavior) */}
            <motion.div {...rise} data-testid="proof-row-3" className="grid lg:grid-cols-2 gap-8 items-center">
              <div data-testid="sharing-control" className="rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-6">
                <p className="text-xs font-bold uppercase tracking-wide text-[#94A3B8]">Per-image share choice</p>
                <div className="mt-4 space-y-2.5">
                  <div className="flex items-center justify-between rounded-xl border border-[#177245]/30 bg-[#177245]/[0.06] px-4 py-3">
                    <span className="flex items-center gap-2 text-sm font-medium text-navy"><Eye size={16} className="text-[#177245]" /> Original</span>
                    <span className="text-xs text-[#177245] font-semibold">Shared as captured</span>
                  </div>
                  <div className="flex items-center justify-between rounded-xl border border-[#E2E8F0] bg-white px-4 py-3">
                    <span className="flex items-center gap-2 text-sm font-medium text-navy"><ImageOff size={16} className="text-brand" /> Redacted copy</span>
                    <span className="text-xs text-[#64748B]">Separate derivative</span>
                  </div>
                  <div className="flex items-center justify-between rounded-xl border border-[#E2E8F0] bg-white px-4 py-3">
                    <span className="flex items-center gap-2 text-sm font-medium text-navy"><EyeOff size={16} className="text-[#94A3B8]" /> Excluded</span>
                    <span className="text-xs text-[#64748B]">Not shared</span>
                  </div>
                </div>
                <p className="mt-4 text-xs text-[#94A3B8] leading-relaxed">A redacted copy is a new file. Your original photo and its fingerprint are never changed.</p>
              </div>
              <div>
                <h3 className="font-heading text-2xl font-semibold text-navy">Share only what you choose</h3>
                <p className="mt-3 text-[#475569] leading-relaxed">For each image in a shared report you decide: share the original, share a redacted copy, or leave it out entirely. Nothing is shared until you choose to share it.</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 5 — Tamper-evident records */}
      <section className="bg-[#0F1B2D] text-white" data-testid="tamper-evident">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20 w-full grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="grid place-items-center h-12 w-12 rounded-2xl bg-brand/15 text-[#6FA2F0]"><Fingerprint size={24} /></span>
            <h2 className="mt-5 font-heading text-3xl sm:text-4xl font-semibold tracking-tight">See what was captured — and whether the stored artifact still matches.</h2>
            <p className="mt-4 text-white/70 leading-relaxed">
              When you lock your documentation, TenantSeal records a SHA-256 fingerprint of each stored file. Anyone you share with can check that fingerprint against the stored record to confirm the bytes haven't changed since it was sealed — without seeing your photos, address, or identity.
            </p>
            <p className="mt-4 text-sm text-white/50 leading-relaxed" data-testid="tamper-limitation">
              A fingerprint shows whether a stored file changed after upload. It does not verify what happened before the file reached TenantSeal.
            </p>
          </div>
          <VerificationChip />
        </div>
      </section>

      {/* 6 — Honest by design */}
      <section className="bg-white text-[#0F172A]" data-testid="honest-by-design">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 sm:py-20 w-full">
          <span className="grid place-items-center h-12 w-12 rounded-2xl bg-brand/10 text-brand"><ShieldQuestion size={24} /></span>
          <h2 className="mt-5 font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-navy">TenantSeal does not pretend to know what it cannot verify.</h2>
          <ul className="mt-8 space-y-3.5">
            {[
              "AI suggestions are drafts you confirm — nothing is treated as your statement until you say so.",
              "Imported material stays labeled as imported.",
              "Recipient responses stay labeled as theirs.",
              "Statements without a confirmed source can't enter a final export.",
              "Nothing sends automatically — you choose when and what to share.",
            ].map((t) => (
              <li key={t} className="flex items-start gap-3 text-[15px] text-[#334155] leading-snug">
                <CheckCircle2 size={18} className="text-[#177245] mt-0.5 shrink-0" /> {t}
              </li>
            ))}
          </ul>
          <p className="mt-8 rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-5 text-[15px] text-[#475569] leading-relaxed" data-testid="honest-limitation">
            TenantSeal helps organize and present a rental record. It does not decide fault, fairness, legal rights, or likely outcomes.
          </p>
        </div>
      </section>

      {/* 7 — Tenant control & takeout */}
      <section className="bg-[#0F1B2D] text-white" data-testid="tenant-control">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20 w-full">
          <div className="max-w-2xl">
            <span className="grid place-items-center h-12 w-12 rounded-2xl bg-brand/15 text-[#6FA2F0]"><Download size={24} /></span>
            <h2 className="mt-5 font-heading text-3xl sm:text-4xl font-semibold tracking-tight">Your rental record should not be trapped inside an app.</h2>
          </div>
          <div className="mt-10 grid md:grid-cols-2 gap-5">
            {[
              { icon: Download, t: "Evidence Takeout", d: "Download a ZIP of one property — or your whole account — with the original stored files and a plain-language manifest of what's included." },
              { icon: ImageOff, t: "Exact share selection", d: "Choose Original, a Redacted copy, or Exclude for every image in a shared report." },
              { icon: Lock, t: "Originals stay untouched", d: "Redacted copies are separate derivatives — your original files and their fingerprints are never modified." },
              { icon: Fingerprint, t: "Public fingerprint verification", d: "A recipient can confirm a sealed record's fingerprint without creating an account. Your underlying evidence stays accessible to you." },
            ].map((c) => (
              <div key={c.t} className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 flex items-start gap-4" data-testid={`control-card-${c.t.split(" ")[0].toLowerCase()}`}>
                <span className="grid place-items-center h-11 w-11 rounded-xl bg-brand/15 text-[#6FA2F0] shrink-0"><c.icon size={20} /></span>
                <div>
                  <h3 className="font-heading text-lg font-semibold">{c.t}</h3>
                  <p className="mt-1.5 text-white/65 leading-relaxed text-[15px]">{c.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 8 — TenantSeal Plus (purple reserved for Plus) */}
      <section className="bg-white text-[#0F172A]" data-testid="tenantseal-plus">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16 sm:py-20 w-full">
          <div className="text-center max-w-2xl mx-auto">
            <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold" style={{ color: PURPLE, backgroundColor: "rgba(124,58,237,0.10)", border: `1px solid rgba(124,58,237,0.25)` }}>
              <Sparkles size={14} /> TenantSeal Plus
            </span>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-navy">Start with the record. Upgrade when you need advanced preparation tools.</h2>
          </div>
          <div className="mt-10 grid md:grid-cols-2 gap-5 max-w-3xl mx-auto">
            <div className="rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-6" data-testid="plan-free">
              <h3 className="font-heading text-lg font-semibold text-navy">Free</h3>
              <p className="mt-1 text-sm text-[#64748B]">Your rental record, kept and yours.</p>
              <ul className="mt-5 space-y-2.5 text-[15px] text-[#475569]">
                {["Guided room-by-room documentation", "Lock & tamper-evident fingerprints", "Public fingerprint verification", "Evidence Takeout (ZIP export)", "Share links with per-image redaction", "One property"].map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm"><CheckCircle2 size={16} className="text-[#177245] mt-0.5 shrink-0" /> {f}</li>
                ))}
              </ul>
              <div className="mt-6"><StartBtn testId="plan-free-cta" source="pricing_free" className="h-12 w-full" /></div>
            </div>
            <div className="rounded-2xl p-6 text-white" style={{ backgroundColor: "#1B1030", border: `1px solid ${PURPLE}` }} data-testid="plan-plus">
              <div className="flex items-center justify-between">
                <h3 className="font-heading text-lg font-semibold">TenantSeal Plus</h3>
                <span className="rounded-full text-white text-xs font-semibold px-2.5 py-0.5" style={{ backgroundColor: PURPLE }}>$6/mo</span>
              </div>
              <p className="mt-1 text-sm text-white/60">Advanced preparation & final-output tools.</p>
              <ul className="mt-5 space-y-2.5 text-[15px] text-white/85">
                {["Unlimited properties", "Maintenance, rent, communication & checkup logs", "Deduction Auditor & Charge Storyboard", "Dispute letter & evidence-cited drafting export", "Exhibit Dossier & escalation packet", "Move-out prep & condition history"].map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm"><CheckCircle2 size={16} className="mt-0.5 shrink-0" style={{ color: "#C4A6FF" }} /> {f}</li>
                ))}
              </ul>
              <div className="mt-6">
                <Button data-testid="plan-plus-cta" onClick={onPlusClick} disabled={loading} className="h-12 w-full rounded-xl text-white hover:opacity-90" style={{ backgroundColor: PURPLE }}>
                  {!user ? "Start documenting" : isPremium ? "Go to dashboard" : planCta.cta}
                </Button>
              </div>
            </div>
          </div>
          <p className="mt-6 text-center text-[13px] text-[#475569] max-w-2xl mx-auto" data-testid="plus-rule">
            Your underlying evidence remains accessible. TenantSeal Plus applies only to the specific preparation and final-output tools marked in the product.
          </p>
          <p className="mt-3 text-center text-xs text-[#94A3B8]">$0 today, then $6/month unless canceled. Cancel anytime. TenantSeal is not a law firm and does not provide legal advice.</p>
        </div>
      </section>

      {/* 9 — Final CTA */}
      <section className="bg-[#0B1220] text-white relative overflow-hidden" data-testid="final-cta">
        <div className="absolute -bottom-40 -left-40 h-[480px] w-[480px] rounded-full bg-brand/20 blur-[120px]" aria-hidden="true" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-20 text-center relative">
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">Start your rental record before you need it.</h2>
          <p className="mt-4 text-lg text-white/70 max-w-xl mx-auto">A few minutes documenting today can preserve details you may need months later.</p>
          <div className="mt-8 flex flex-col sm:flex-row justify-center items-center gap-3">
            <StartBtn testId="final-cta-button" source="final_band" className="h-14 px-8 text-base shadow-[0_10px_30px_rgba(30,77,140,0.45)]" />
            <SampleBtn testId="final-cta-demo-link" source="final_band" dark className="h-14 px-6" />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="mt-auto border-t border-white/10 bg-[#0B1220]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <Logo dark size={24} />
            <div className="flex items-center gap-4">
              <Link to="/help" data-testid="footer-help-link" className="text-sm text-white/60 hover:text-white font-medium">Help Center</Link>
              <Link to="/privacy" data-testid="footer-privacy-link" className="text-sm text-white/60 hover:text-white font-medium">Privacy</Link>
              <Link to="/terms" data-testid="footer-terms-link" className="text-sm text-white/60 hover:text-white font-medium">Terms</Link>
              <p className="text-sm text-white/40">© 2026 TenantSeal. Built for tenants.</p>
            </div>
          </div>
          <p className="text-xs text-white/40 text-center sm:text-left" data-testid="legal-disclaimer">
            TenantSeal is not a law firm and does not provide legal advice.
          </p>
        </div>
      </footer>
      <SiteFooter />
    </div>
  );
}

function VerificationChip() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let live = true;
    api.get("/public/demo-verification").then(({ data }) => { if (live) setD(data); }).catch(() => { if (live) setErr(true); });
    return () => { live = false; };
  }, []);
  const shortFp = d?.sha256 ? `${d.sha256.slice(0, 10)}…${d.sha256.slice(-8)}` : null;
  const sealed = d?.sealed_at ? new Date(d.sealed_at).toLocaleDateString() : null;
  // If no safe sample verification record is available, hide the sample row entirely.
  // The surrounding section still explains how public fingerprint verification works.
  if (err) return null;
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6" data-testid="verification-chip">
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs font-bold uppercase tracking-wide text-white/50">Sample verification</span>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#177245]/15 border border-[#177245]/30 px-2.5 py-0.5 text-xs font-semibold text-[#5FD39B]">
          <ShieldCheck size={13} /> {d?.verified ? "Sealed" : "…"}
        </span>
      </div>
      {!d ? (
        <p className="mt-4 text-sm text-white/50 inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading a real sample record…</p>
      ) : (
        <>
          <div className="mt-4 rounded-xl bg-black/30 border border-white/10 p-3">
            <p className="text-[11px] uppercase tracking-wide text-white/40">SHA-256 fingerprint</p>
            <p className="mt-1 font-mono text-sm text-[#6FA2F0] break-all" data-testid="chip-fingerprint">{shortFp}</p>
          </div>
          <div className="mt-3 flex items-center gap-2 text-sm text-white/80" data-testid="chip-status">
            <CheckCircle2 size={16} className="text-[#5FD39B]" /> Fingerprint matches the sealed record{sealed ? ` · sealed ${sealed}` : ""}
          </div>
          <a href={d.verify_url} data-testid="chip-verify-link" className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#6FA2F0] hover:text-white underline underline-offset-4">
            <Fingerprint size={15} /> Open public verification
          </a>
        </>
      )}
    </div>
  );
}
